/* eslint-disable import/no-cycle */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable no-plusplus */
/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */

import { action, computed, thunk, Action, Thunk, Computed } from 'easy-peasy';
import { AxiosError } from 'axios';
import { History } from 'history';

import {
  FormItemT,
  DraggedItem,
  DropTarget,
  TextWithTranslationsT,
  HireFormByIdTypeResponseT,
  ValidationErrorT,
  SerializablePathMaps,
  FormSectionT,
  FormFieldT,
  NormalizedFormState,
  ValidationError,
} from '../types';
import { removeItemByPath } from '../utils';
import { addToCanvas, addToFollowup, addToSection } from '../reducers/addItem';
import { moveToCanvas, moveToFollowUp, moveToSection } from '../reducers/moveItem';
import { generateSectionSchema } from '../utils/generateSectionSchema';
import { generateSchemaSignature, buildPathMaps, serializePathMaps, isStructuralUpdate } from '../utils/pathMapUtils';
import { resolveErrorStructure, getQuestionIdFromPath } from '../utils/resolveTargetIdFromError';
import { HttpStoreModel } from '../../../../http/models/httpModel';
import { HRFormController } from './formBuilderController';
import HttpClientWithRetry from '../../../../http/client/HttpClientWithRetry';
import { PATH_ADMINISTRATION_TEMPLATES } from '../../../../routes/constants';

// --- Types & Interfaces ---
export type FormMode = 'edit' | 'create' | 'copy';
export type FormAddItemPayload = { draggedItem: DraggedItem; dropTarget: DropTarget; optionId?: string };
export type FormBuilderStoreModel = { formBuilder: FormBuilderModel };

export type InitialFormSchema = {
  formSchema: FormItemT[];
  defaultLanguage: string;
  currentLanguage: string;
  availableLanguages: string[];
  templateName: string;
  needFinalSignature: boolean;
  disclaimerText: TextWithTranslationsT | null;
};

export interface FormBuilderState extends NormalizedFormState {
  initialFormSchema: InitialFormSchema | null;
  formSchema: FormItemT[];
  pathMaps: SerializablePathMaps | null;
  draggedItem: DraggedItem | null;
  dropIndicator?: DropTarget & { show: boolean };
  defaultLanguage: string;
  currentLanguage: string;
  availableLanguages: string[];
  templateName: string;
  needFinalSignature: boolean;
  disclaimerText?: TextWithTranslationsT | null;
  isLoading: boolean;
  isUpdating: boolean;
  scrollToItemId?: string | null;
  activeId?: string | null;
  formMode: FormMode;
  showExitPrompt: boolean;
  validationErrors: ValidationError[] | null;
  uploadingDocument: boolean;
  tags: string[];
}

export interface FormBuilderModel extends FormBuilderState {
  // Schema Actions
  setSchema: Action<FormBuilderModel, FormItemT[]>;
  updatePathMaps: Action<FormBuilderModel>;
  rebuildPathMaps: Action<FormBuilderModel>;
  updateItem: Action<FormBuilderModel, { path: number[]; updatedItem: Partial<FormItemT> }>;
  removeItemByPath: Action<FormBuilderModel, { path: number[] }>;

  // Drag & Drop
  startDrag: Action<FormBuilderModel, DraggedItem>;
  endDrag: Action<FormBuilderModel>;
  setDropIndicator: Action<FormBuilderModel, (DropTarget & { show: boolean }) | undefined>;
  addItemToCanvas: Thunk<FormBuilderModel, FormAddItemPayload>;
  addItemToSection: Thunk<FormBuilderModel, FormAddItemPayload>;
  addItemToFollowup: Thunk<FormBuilderModel, FormAddItemPayload>;
  moveItemToCanvas: Thunk<FormBuilderModel, FormAddItemPayload>;
  moveItemToSection: Thunk<FormBuilderModel, FormAddItemPayload>;
  moveItemToFollowup: Thunk<FormBuilderModel, FormAddItemPayload>;

  // Form Metadata Actions
  initializeLanguages: Action<
    FormBuilderModel,
    { availableLanguages: string[]; currentLanguage: string; defaultLanguage: string }
  >;
  setCurrentLanguage: Action<FormBuilderModel, string>;
  setAvailableLanguages: Action<FormBuilderModel, string[]>;
  setDefaultLanguage: Action<FormBuilderModel, string>;
  setTemplateName: Action<FormBuilderModel, string>;
  setNeedFinalSignature: Action<FormBuilderModel, boolean>;
  setDisclaimerText: Action<FormBuilderModel, TextWithTranslationsT | null>;
  setTags: Action<FormBuilderModel, string[]>;

  // UI & Loading States
  setLoading: Action<FormBuilderModel, boolean>;
  setUpdating: Action<FormBuilderModel, boolean>;
  setActiveId: Action<FormBuilderModel, string | null>;
  clearActiveId: Action<FormBuilderModel>;
  setScrollToItemId: Action<FormBuilderModel, string | null>;
  setFormMode: Action<FormBuilderModel, FormMode>;
  setClearState: Action<FormBuilderModel>;
  setShowExitPrompt: Action<FormBuilderModel, boolean>;
  setUploadingDocument: Action<FormBuilderModel, boolean>;

  // Error Management
  setErrors: Action<FormBuilderModel, ValidationErrorT[] | undefined>;
  setError: Action<FormBuilderModel, ValidationError>;
  setValidationErrors: Action<FormBuilderModel, ValidationError[]>;
  removeError: Action<FormBuilderModel, { targetId: string; innerPath?: string }>;
  errorsByTarget: Computed<FormBuilderModel, (targetId: string, property?: string, index?: number) => string | undefined>;
  cleanedValidationErrors: Thunk<FormBuilderModel>;

  // Data Actions
  setInitialFormSchema: Action<FormBuilderModel, InitialFormSchema>;
  setTemplateData: Action<FormBuilderModel, HireFormByIdTypeResponseT>;
  setNormalizedData: Action<FormBuilderModel, FormItemT[]>;

  // Thunks (API)
  getTags: Thunk<FormBuilderModel, { account: string }, void, HttpStoreModel, Promise<any>>;
  uploadDocument: Thunk<FormBuilderModel, { account: string; payload: FormData }, void, HttpStoreModel, Promise<string>>;
  getTemplateById: Thunk<
    FormBuilderModel,
    { account: string; id: string; history: History },
    void,
    HttpStoreModel,
    Promise<HireFormByIdTypeResponseT | undefined>
  >;
  checkUniqueTemplateName: Thunk<FormBuilderModel, { account: string; name: string }, void, HttpStoreModel, Promise<string>>;
  createTemplate: Thunk<FormBuilderModel, { account: string; payload: any }, void, HttpStoreModel, Promise<any>>;
  updateTemplate: Thunk<FormBuilderModel, { account: string; id: string; payload: any }, void, HttpStoreModel, Promise<any>>;
}

const defaultState: FormBuilderState = {
  formSchema: [],
  pathMaps: null,
  draggedItem: null,
  dropIndicator: undefined,
  defaultLanguage: 'en',
  currentLanguage: 'en',
  availableLanguages: ['en'],
  templateName: '',
  needFinalSignature: false,
  disclaimerText: null,
  isLoading: false,
  isUpdating: false,
  scrollToItemId: null,
  activeId: null,
  formMode: 'create',
  showExitPrompt: false,
  validationErrors: null,
  initialFormSchema: null,
  uploadingDocument: false,
  tags: [],
  fieldsById: {},
  sectionsById: {},
};

// --- Model Implementation ---
export const formBuilderModel: FormBuilderModel = {
  ...defaultState,

  // --- Schema Actions ---
  setSchema: action((state, payload) => {
    state.formSchema = payload;
    state.showExitPrompt = true;
    state.dropIndicator = undefined;
    state.scrollToItemId = null;

    const signature = generateSchemaSignature(payload);
    if (!state.pathMaps || state.pathMaps.schemaSignature !== signature) {
      const pathMaps = buildPathMaps(payload);
      state.pathMaps = serializePathMaps(pathMaps, signature);
    }
  }),

  updatePathMaps: action((state) => {
    if (!state.formSchema?.length) {
      state.pathMaps = null;
      return;
    }
    const signature = generateSchemaSignature(state.formSchema);
    if (state.pathMaps?.schemaSignature === signature) return;

    const pathMaps = buildPathMaps(state.formSchema);
    state.pathMaps = serializePathMaps(pathMaps, signature);
  }),

  rebuildPathMaps: action((state) => {
    if (!state.formSchema?.length) {
      state.pathMaps = null;
      return;
    }
    const signature = generateSchemaSignature(state.formSchema);
    const pathMaps = buildPathMaps(state.formSchema);
    state.pathMaps = serializePathMaps(pathMaps, signature);
  }),

  updateItem: action((state, { path, updatedItem }) => {
    // const newSchema = deepClone(state.formSchema);
    let current = state.formSchema;
    let item: any = null;

    for (let i = 0; i < path.length; i++) {
      const idx = path[i];
      if (i === path.length - 1) {
        item = current[idx];
      } else {
        const next = current[idx];
        if ('fields' in next) current = next.fields;
        else if (next.inputType === 'yesNo' && next.followUpQuestionList) current = next.followUpQuestionList;
        else return;
      }
    }

    if (item) {
      Object.assign(item, updatedItem);
      state.formSchema = current;

      if (isStructuralUpdate(updatedItem)) {
        const signature = generateSchemaSignature(current);
        const pathMaps = buildPathMaps(current);
        state.pathMaps = serializePathMaps(pathMaps, signature);
      }
    }

    state.draggedItem = null;
    state.dropIndicator = undefined;
    state.scrollToItemId = null;
  }),

  removeItemByPath: action((state, { path }) => {
    const updated = removeItemByPath(state.formSchema, path);
    if (updated) {
      state.formSchema = updated;
      const signature = generateSchemaSignature(updated);
      const pathMaps = buildPathMaps(updated);
      state.pathMaps = serializePathMaps(pathMaps, signature);
    }
    state.scrollToItemId = null;
  }),

  // --- Drag & Drop Thunks ---
  startDrag: action((state, payload) => {
    state.draggedItem = payload;
    state.dropIndicator = undefined;
    state.scrollToItemId = null;
  }),

  endDrag: action((state) => {
    state.draggedItem = null;
    state.dropIndicator = undefined;
    state.scrollToItemId = null;
  }),

  setDropIndicator: action((state, payload) => {
    state.dropIndicator = state.draggedItem ? payload : undefined;
  }),

  addItemToCanvas: thunk((actions, payload, { getState }) => {
    const { formSchema, scrollToItemId } = addToCanvas(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
    actions.setScrollToItemId(scrollToItemId);
  }),

  addItemToSection: thunk((actions, payload, { getState }) => {
    const { formSchema, scrollToItemId } = addToSection(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
    actions.setScrollToItemId(scrollToItemId);
  }),

  addItemToFollowup: thunk((actions, payload, { getState }) => {
    const { formSchema } = addToFollowup(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
  }),

  moveItemToCanvas: thunk((actions, payload, { getState }) => {
    const { formSchema } = moveToCanvas(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
  }),

  moveItemToSection: thunk((actions, payload, { getState }) => {
    const { formSchema } = moveToSection(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
  }),

  moveItemToFollowup: thunk((actions, payload, { getState }) => {
    const { formSchema } = moveToFollowUp(getState(), payload);
    actions.setSchema(formSchema);
    actions.endDrag();
  }),

  // --- Form Settings Actions ---
  initializeLanguages: action((state, { availableLanguages, currentLanguage, defaultLanguage }) => {
    const unique = Array.from(new Set(availableLanguages.filter(Boolean)));

    // Only update if we have valid languages
    if (unique.length > 0) {
      state.availableLanguages = unique;
    }

    if (currentLanguage && state.availableLanguages.includes(currentLanguage)) {
      state.currentLanguage = currentLanguage;
    }

    if (defaultLanguage && state.availableLanguages.includes(defaultLanguage)) {
      state.defaultLanguage = defaultLanguage;
    }

    state.showExitPrompt = true;
  }),

  setCurrentLanguage: action((state, lang) => {
    if (state.availableLanguages.includes(lang)) state.currentLanguage = lang;
    state.showExitPrompt = true;
  }),

  setAvailableLanguages: action((state, langs) => {
    state.availableLanguages = Array.from(new Set(langs.filter(Boolean)));
    state.showExitPrompt = true;
  }),

  setDefaultLanguage: action((state, lang) => {
    if (state.availableLanguages.includes(lang)) state.defaultLanguage = lang;
    state.showExitPrompt = true;
  }),

  setTemplateName: action((state, name) => {
    state.templateName = name;
    state.showExitPrompt = true;
  }),

  setNeedFinalSignature: action((state, value) => {
    state.needFinalSignature = value;
    state.showExitPrompt = true;
  }),

  setDisclaimerText: action((state, value) => {
    state.disclaimerText = value;
    state.showExitPrompt = true;
  }),

  // --- UI State Actions ---
  setLoading: action((state, value) => {
    state.isLoading = value;
  }),
  setUpdating: action((state, value) => {
    state.isUpdating = value;
  }),
  setActiveId: action((state, id) => {
    state.activeId = id;
  }),
  clearActiveId: action((state) => {
    state.activeId = null;
  }),
  setScrollToItemId: action((state, id) => {
    state.scrollToItemId = id;
  }),
  setFormMode: action((state, mode) => {
    state.formMode = mode;
  }),
  setShowExitPrompt: action((state, value) => {
    state.showExitPrompt = value;
  }),
  setUploadingDocument: action((state, payload) => {
    state.uploadingDocument = payload;
  }),
  setTags: action((state, payload) => {
    state.tags = payload;
  }),

  setClearState: action((state) => {
    Object.assign(state, {
      ...defaultState,
      formMode: 'create',
      showExitPrompt: false,
    });
  }),

  // --- Error Handling ---
  setValidationErrors: action((state, errors) => {
    state.validationErrors = errors;
  }),
  setErrors: action((state, errors) => {
    if (!errors?.length || !state.pathMaps) {
      state.validationErrors = [];
      return;
    }

    const uniqueErrorsMap = new Map<string, ValidationError>();
    errors.forEach((err) => {
      const resolved = resolveErrorStructure(err, state.pathMaps!);
      const errorObj = {
        targetId: resolved?.targetId,
        innerPath: resolved?.innerPath || '',
        fullPath: resolved?.fullPath || err.path.join('.'),
        message: err.message,
      };
      if (!uniqueErrorsMap.has(errorObj.fullPath)) {
        uniqueErrorsMap.set(errorObj.fullPath, errorObj);
      }
    });

    state.validationErrors = Array.from(uniqueErrorsMap.values());
  }),

  setError: action((state, error) => {
    if (!state.pathMaps) return;
    const existingIndex =
      state.validationErrors?.findIndex((e) => e.targetId === error?.targetId && e.innerPath === error?.innerPath) ?? -1;

    if (existingIndex !== -1 && state.validationErrors) {
      state.validationErrors[existingIndex] = error;
    } else {
      state.validationErrors = [...(state.validationErrors || []), error];
    }
  }),

  removeError: action((state, { targetId, innerPath }) => {
    if (!state.validationErrors) return;
    state.validationErrors = state.validationErrors.filter((error) => {
      const isTargetMatch = (error.targetId || 'root') === targetId;
      return innerPath ? !(isTargetMatch && error.innerPath === innerPath) : !isTargetMatch;
    });
  }),

  errorsByTarget: computed(
    [(state) => state.validationErrors],
    (validationErrors) => (targetId: string, property?: string, index?: number) => {
      if (!validationErrors) return undefined;
      const searchPath = property ? (index !== undefined ? `${property}.${index}` : property) : '';

      return validationErrors.find((e) => {
        const isTargetMatch = (e.targetId || 'root') === targetId;
        return property ? isTargetMatch && e.innerPath === searchPath : isTargetMatch;
      })?.message;
    },
  ),

  cleanedValidationErrors: thunk((actions, _payload, { getState }) => {
    const { pathMaps, validationErrors } = getState();

    // Early return if no cleanup needed
    if (!pathMaps || !validationErrors || validationErrors.length === 0) {
      return;
    }

    // Create Sets for O(1) lookup performance
    const validFieldIds = new Set<string>(Object.keys(pathMaps.fieldIdToIndex));

    const validSectionAliases = new Set<string>(Object.keys(pathMaps.sectionToFirstField));

    // Filter errors - keep only valid ones
    const cleanedErrors = validationErrors.filter((error) => {
      // Keep errors without targetId (root-level errors)
      if (!error.targetId) {
        return true;
      }

      // Keep errors where targetId still exists in pathMaps
      return validFieldIds.has(error.targetId) || validSectionAliases.has(error.targetId);
    });

    // Only update if something actually changed (performance optimization)
    if (cleanedErrors.length !== validationErrors.length) {
      actions.setValidationErrors(cleanedErrors);
    }
  }),

  // --- Data & API Thunks ---
  setInitialFormSchema: action((state, schema) => {
    state.initialFormSchema = schema;
  }),

  setTemplateData: action((state, payload) => {
    if (!payload) return;
    const formSchema = generateSectionSchema({ currentTemplate: payload.questions }) || [];

    state.formSchema = formSchema;
    state.templateName = payload.name ?? '';
    state.needFinalSignature = payload.needFinalSignature ?? false;
    state.availableLanguages = payload.languages ?? [];
    state.currentLanguage = payload.defaultLanguage ?? 'en';
    state.defaultLanguage = payload.defaultLanguage ?? 'en';
    state.disclaimerText = payload.signatureDetails?.disclaimerText ?? null;

    state.initialFormSchema = {
      formSchema,
      templateName: state.templateName,
      needFinalSignature: state.needFinalSignature,
      availableLanguages: state.availableLanguages,
      currentLanguage: state.currentLanguage,
      defaultLanguage: state.defaultLanguage,
      disclaimerText: state.disclaimerText,
    };

    const signature = generateSchemaSignature(formSchema);
    const pathMaps = buildPathMaps(formSchema);
    state.pathMaps = serializePathMaps(pathMaps, signature);
  }),

  setNormalizedData: action((state, backendFields) => {
    const fieldsById: Record<string, FormFieldT> = {};
    const sectionsById: Record<string, FormSectionT> = {};

    const flatten = (items: any[], isFollowUp: boolean) => {
      items.forEach((item) => {
        fieldsById[item.id] = { ...item, isFollowUp, followUpQuestionList: null };
        if (item.section && !sectionsById[item.section]) {
          sectionsById[item.section] = {
            id: item.section,
            section: item.section,
            sectionTitle: item.sectionTitle || { en: item.section },
            fields: [],
          };
        }
        if (item.followUpQuestionList?.length) flatten(item.followUpQuestionList, true);
      });
    };

    flatten(backendFields, false);
    state.fieldsById = fieldsById;
    state.sectionsById = sectionsById;
  }),

  createTemplate: thunk(async (actions, { account, payload }, { getStoreState }) => {
    try {
      actions.setUpdating(true);
      const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.createTemplate(account, payload);
      actions.setErrors([]);
      return response?.data;
    } catch (error) {
      const newErr = error as AxiosError<any>;
      if (newErr.response?.status === 409) {
        actions.setError({ fullPath: 'value', innerPath: 'name', message: newErr.response.data.errors });
      } else {
        const rawErrors = newErr.response?.data?.errors;
        const formattedErrors = Array.isArray(rawErrors)
          ? rawErrors.map((e) => ({ ...e, questionId: getQuestionIdFromPath(payload.questions, e.path) }))
          : [];
        actions.setErrors(formattedErrors);
      }
      throw newErr;
    } finally {
      actions.setUpdating(false);
    }
  }),

  updateTemplate: thunk(async (actions, { account, id, payload }, { getStoreState }) => {
    try {
      actions.setUpdating(true);
      const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.updateTemplate(account, id, payload);
      actions.setErrors([]);
      return response?.data?.data?.templateId;
    } catch (error) {
      const newErr = error as AxiosError<any>;
      if (newErr.response?.status === 409) {
        actions.setError({ targetId: '', fullPath: 'value', innerPath: 'name', message: newErr.response.data.errors });
      } else {
        actions.setErrors(newErr.response?.data?.errors ?? []);
      }
      throw newErr;
    } finally {
      actions.setUpdating(false);
    }
  }),

  getTemplateById: thunk(async (actions, { account, id, history }, { getStoreState }) => {
    try {
      actions.setLoading(true);
      const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.getTemplateById(account, id);
      actions.setTemplateData(response?.data);
      return response?.data;
    } catch (error) {
      if ((error as any).response?.status === 401) history.replace(PATH_ADMINISTRATION_TEMPLATES);
    } finally {
      actions.setLoading(false);
    }
  }),

  checkUniqueTemplateName: thunk(async (_, { account, name }, { getStoreState }) => {
    const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
    const response = await controller.checkUniqueTemplateName(account, name);
    return response?.data?.suggestedName;
  }),

  uploadDocument: thunk(async (actions, { account, payload }, { getStoreState }) => {
    try {
      actions.setUploadingDocument(true);
      const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.uploadDocument(account, payload);
      return response?.data?.data?.url;
    } finally {
      actions.setUploadingDocument(false);
    }
  }),

  getTags: thunk(async (actions, { account }, { getStoreState }) => {
    try {
      const controller = new HRFormController(getStoreState().http.client as HttpClientWithRetry);
      const response = await controller.getTags(account);
      actions.setTags(response?.data);
      return response?.data;
    } catch (error) {
      actions.setTags([]);
      throw error;
    }
  }),
};
